/**
 * Machine-readable session failure codes checkout-gateway returns, mirrored
 * from its `CheckoutSessionErrorCodes` enum.
 *
 * These are a field on the JSON error body, alongside the human-readable text
 * — not an HTTP status:
 *
 * ```json
 * { "message": "Checkout is unavailable, please reopen the store", "errorCode": "SESSION_ENDED" }
 * ```
 *
 * That is what makes them worth forwarding: the status is ambiguous (a 401
 * alone cannot tell session expiry from a bad token) and `message` is English
 * prose that may be reworded, so callers branch on these instead.
 *
 * They reach publishers as `error.code` on the SDK postMessage payload — the
 * analytics DTOs are unchanged and still carry only `reason`. Publishers group
 * on them, so they are stable identifiers: renaming one breaks grouping on
 * already-shipped data.
 */
export const CheckoutSessionErrorCode = {
  /** Session TTL elapsed, idle timeout, or the player cancelled — session removed. */
  SESSION_ENDED: 'SESSION_ENDED',
  /** Checkout reopened or refreshed; the session was already consumed or closed. */
  SESSION_ALREADY_USED: 'SESSION_ALREADY_USED',
  /** Missing, malformed or unrecognised checkout token. */
  SESSION_INVALID: 'SESSION_INVALID',
  /** Unhandled application error on the gateway side. */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export type CheckoutSessionErrorCode = (typeof CheckoutSessionErrorCode)[keyof typeof CheckoutSessionErrorCode];
